import * as React from 'react'

import EmptyState from '../empty_state'
import { StudentPracticeInterface, } from '../interfaces'
import { formatDateToMMDDYY, ALL, accountImg, TABLE_WIDTH_PROPS, } from '../shared'
import { getRoundedTotalTimeSpentString, } from '../../../../helpers/studentReports'
import { DataTable, Tooltip, Spinner, } from '../../../../../Shared/index'
import GradeColor, { GREEN, ORANGE, RED, } from '../../../modules/grade_color'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'

interface ByStudentPropsInterface {
  studentPractices: StudentPracticeInterface[];
  selectedSkill: { value: number|string, label: string, };
  allSkillNames?: string[];
}

const ByStudent = ({ studentPractices, selectedSkill, allSkillNames, }: ByStudentPropsInterface) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const allSelected = selectedSkill?.value === ALL
  const relevantStudentPractice = studentPractices?.find(studentPractice => studentPractice.skills.find(skill => skill.skill_name === selectedSkill?.value))

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 240, minWidth: 240 },
      { width: 240 },
      { width: 240 },
      { width: 240 },
      { width: 240 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  const headers = [
    {
      name: 'Student',
      attribute: 'name',
      width: columnWidths[0],
      rowSectionClassName: 'name border-right',
      renderCellsAsHeaders: true,
    },
    {
      name: 'Total Activities Completed',
      attribute: 'activityCount',
      width: columnWidths[1],
      textAlign: 'left',
      headerClassName: 'eight-px-right-padding'
    },
    {
      name: 'Total Time Spent',
      attribute: 'timespentForDisplay',
      width: columnWidths[2],
    },
    {
      name: 'Last Practiced',
      attribute: 'lastPracticed',
      width: columnWidths[3],
    },
    {
      name: 'Results',
      attribute: 'results',
      width: columnWidths[4],
      rowSectionClassName: 'show-overflow'
    },
  ]

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !!(allSelected || relevantStudentPractice),
    dependencies: [columnWidths.join('|')], // ensure resync when columnWidths change
  });

  if (!studentPractices) {
    return (
      <div className="diagnostic-insights-content practice-insights">
        <Spinner />
      </div>
    )
  }

  if (!studentPractices.filter(sp => sp.skills?.length).length || !(allSelected || relevantStudentPractice)) {
    return (
      <EmptyState
        bodyText="There is no practice history available for the filters selected. However, there may be history for previous versions of the selected diagnostic. Try selecting another version from the diagnostic dropdown or modifying the other filters. Alternatively, consider assigning a diagnostic."
        headerText="No practice insights are available"
      />
    )
  }

  const renderResults = (percentages) => {
    let greenActivityCount = 0
    let orangeActivityCount = 0
    let redActivityCount = 0

    greenActivityCount = percentages.filter(percentage => GradeColor(percentage) === GREEN)?.length || 0
    orangeActivityCount = percentages.filter(percentage => GradeColor(percentage) === ORANGE)?.length || 0
    redActivityCount = percentages.filter(percentage => GradeColor(percentage) === RED)?.length || 0

    return (
      <div className="score-boxes">
        <Tooltip
          tooltipText={`A green activity square indicates that the student frequently demonstrated the relevant writing skill and earned a percentage score of 83-100%. Total activities in this range: ${greenActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Frequently demonstrated skill on ${greenActivityCount} practice ${greenActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box green">{greenActivityCount}</div>}
        />
        <Tooltip
          tooltipText={`A yellow activity square indicates that the student sometimes demonstrated the relevant writing skill and earned a percentage score of 32-82%. Total activities in this range: ${orangeActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Sometimes demonstrated skill on ${orangeActivityCount} practice ${orangeActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box orange">{orangeActivityCount}</div>}
        />
        <Tooltip
          tooltipText={`A red activity square indicates that the student rarely demonstrated the relevant writing skill and earned a percentage score of 0-31%. Total activities in this range: ${redActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Rarely demonstrated skill on ${redActivityCount} practice ${redActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box red">{redActivityCount}</div>}
        />
      </div>
    )
  }

  const rows = () => {
    return studentPractices.map(studentPractice => {
      const { user_id, user_name, skills, } = studentPractice

      let activityCount
      let timespentForDisplay
      let lastPracticed
      let results

      if (allSelected) {
        const lastPracticedDatesToNumbers = skills.map(sp => Number(new Date(sp?.last_practiced)))

        activityCount = skills.reduce((sum, skillPractice) => sum + skillPractice.activity_count, 0)
        timespentForDisplay = skills.length ? getRoundedTotalTimeSpentString(skills.map((skillPractice) => skillPractice.timespent).flat()) : '—'
        lastPracticed = skills.length ? formatDateToMMDDYY(Math.max(...lastPracticedDatesToNumbers)) : '—'
        results = renderResults(skills.map(sp => sp.percentages).flat() || [])
      } else {
        const relevantSkillPractice = skills.find(skill => skill.skill_name === selectedSkill.value)
        activityCount = relevantSkillPractice?.activity_count || '—'
        timespentForDisplay = relevantSkillPractice?.timespent ? getRoundedTotalTimeSpentString(relevantSkillPractice.timespent) : '—'
        lastPracticed = relevantSkillPractice?.last_practiced ? formatDateToMMDDYY(relevantSkillPractice.last_practiced) : '—'
        results =  renderResults(relevantSkillPractice?.percentages || [])
      }

      return {
        id: user_id,
        name: <span className="student-name">{accountImg}{user_name}</span>,
        activityCount,
        timespentForDisplay,
        lastPracticed,
        results
      }
    })
  }

  return (
    <section className="diagnostic-insights-content practice-insights" ref={containerRef}>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows()}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}
export default ByStudent
